import { useMemo, useState } from "react";
import { Badge } from "flowbite-react";
import { HiChevronDown, HiChevronUp } from "react-icons/hi";

const HistoricoConsultas = ({
  historicoConsultas = [],
  quantidadeTotal,
  periodoInicial,
  periodoFinal,
}) => {
  const [expandido, setExpandido] = useState(false);

  const parseDate = (raw) => {
    if (!raw) return null;
    if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw;
    const s = String(raw).trim();
    if (!s) return null;
    // dd/mm/yyyy
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(s)) {
      const [dd, mm, yyyy] = s.split("/");
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    // yyyy-mm-dd
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [yyyy, mm, dd] = s.split("-");
      const d = new Date(Number(yyyy), Number(mm) - 1, Number(dd));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(s);
    return Number.isNaN(d.getTime()) ? null : d;
  };

  const formatTimeAgo = (raw) => {
    const d = parseDate(raw);
    if (!d) return null;
    const now = new Date();
    const diffMs = now - d;
    if (!Number.isFinite(diffMs)) return null;
    const diffDays = Math.floor(diffMs / 86400000);
    if (diffDays < 0) return "no futuro";
    if (diffDays === 0) return "hoje";
    if (diffDays === 1) return "há 1 dia";
    if (diffDays < 30) return `há ${diffDays} dias`;
    const diffMonths = Math.floor(diffDays / 30);
    if (diffMonths === 1) return "há 1 mês";
    if (diffMonths < 12) return `há ${diffMonths} meses`;
    const diffYears = Math.floor(diffMonths / 12);
    return diffYears === 1 ? "há 1 ano" : `há ${diffYears} anos`;
  };

  const LIMITE = 5;
  const total = historicoConsultas.length;
  const restantes = Math.max(total - LIMITE, 0);

  const listaExibida = useMemo(
    () =>
      expandido ? historicoConsultas : historicoConsultas.slice(0, LIMITE),
    [expandido, historicoConsultas]
  );

  return (
    <>
      {(Number.isFinite(quantidadeTotal) || periodoInicial) && (
        <div className="grid gap-3 mb-4 sm:grid-cols-2 lg:grid-cols-3">
          {Number.isFinite(quantidadeTotal) && (
            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-1 text-sm shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                Quantidade total
              </div>
              <div className="text-lg font-semibold text-slate-900">
                {quantidadeTotal}
              </div>
            </div>
          )}

          {periodoInicial && periodoFinal && (
            <div className="flex items-center justify-between rounded-2xl border border-slate-200 bg-white px-4 py-1 text-sm shadow-sm">
              <div className="text-[10px] font-semibold uppercase tracking-[0.25em] text-slate-500">
                Período
              </div>
              <div className="text-sm font-semibold text-slate-900">
                {periodoInicial} até {periodoFinal}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Tabela + Fade quando colapsado */}
      <div className="relative mt-2 overflow-hidden rounded-2xl border border-slate-200 bg-white">
        <div className="border-b border-slate-200 bg-slate-50 px-4 py-3">
          <div className="grid gap-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-slate-500 grid-cols-2">
            <div>Data</div>
            <div>Empresa</div>
          </div>
        </div>

        {/* Área com transição de altura */}
        <div
          className={`transition-all duration-300 ease-out ${
            expandido ? "max-h-[9999px]" : "max-h-auto"
          } overflow-hidden`}
        >
          {listaExibida.map((consulta, index) => (
            <div
              key={index}
              className={`grid  items-center gap-4 px-4 py-3 text-sm transition-colors grid-cols-2 ${
                index % 2 === 0 ? "bg-white" : "bg-slate-50/40"
              } hover:bg-slate-50`}
            >
              <div className="font-medium text-slate-700">
                <span>{consulta?.data || "-"}</span>
                {consulta?.data && (
                  <span className="ml-2 text-[11px] text-slate-400">
                    ({formatTimeAgo(consulta?.data) || ""})
                  </span>
                )}
              </div>
              <div className="font-medium text-slate-900 truncate">
                {consulta?.empresa || "-"}
              </div>
            </div>
          ))}
        </div>

        {/* Fade indicando que há mais quando colapsado */}
        {!expandido && total > LIMITE && (
          <div className="pointer-events-none absolute inset-x-0 bottom-[52px] h-16 bg-gradient-to-t from-white to-white/0" />
        )}

        {/* Footer com contador + botão */}
        {total > LIMITE && (
          <div className="flex items-center justify-between gap-3 border-t border-slate-200 bg-white px-4 py-3">
            <button
              type="button"
              onClick={() => setExpandido((v) => !v)}
              aria-expanded={expandido}
              className="inline-flex items-center gap-2 rounded-xl border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 active:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-slate-300"
            >
              {expandido ? (
                <>
                  <HiChevronUp className="text-lg" />
                  Ver menos
                </>
              ) : (
                <>
                  <HiChevronDown className="text-lg" />
                  Ver mais
                </>
              )}
            </button>
            <div className="flex items-center gap-2 text-xs text-slate-600">
              <Badge color="gray">
                Exibindo {Math.min(LIMITE, total)} de {total}
              </Badge>
              {!expandido && restantes > 0 && (
                <span className="text-slate-500">(+{restantes} restantes)</span>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Aviso quando colapsado */}
      {!expandido && total > LIMITE && (
        <div className="text-sm mt-3 text-slate-500 text-center">
          Exibindo as últimas {LIMITE} ocorrências.
        </div>
      )}
    </>
  );
};

export default HistoricoConsultas;
